import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Spinner,
} from '@fluentui/react-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ExportConfirmDialogProps {
    open: boolean;
    // Number of chats selected — shown in the copy and used to word the warning.
    count: number;
    // True while the export request is in flight; keeps the buttons disabled so a double-click can't
    // fire two exports (each of which would consume the 14-day window).
    busy: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Confirms a chat-history export and warns that exporting is limited to once every 14 days — so the
 * user can back out before spending that window (a partial selection still consumes it). Only on
 * confirm is the export endpoint called.
 */
export const ExportConfirmDialog: FC<ExportConfirmDialogProps> = ({ open, count, busy, onConfirm, onCancel }) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onOpenChange={(_, data) => {
                // Dismiss (Esc / backdrop) counts as cancel; never mid-export so we don't strand a
                // pending request.
                if (!data.open && !busy) {
                    onCancel();
                }
            }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{t('chatHistory.exportConfirmTitle')}</DialogTitle>
                    <DialogContent>{t('chatHistory.exportConfirmBody', { count })}</DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" disabled={busy} onClick={onCancel}>
                            {t('chatHistory.exportCancel')}
                        </Button>
                        <Button
                            appearance="primary"
                            disabled={busy}
                            icon={busy ? <Spinner size="tiny" /> : undefined}
                            onClick={onConfirm}
                            data-testid="export-confirm"
                        >
                            {t('chatHistory.exportConfirmButton')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
